import { useEffect, useState, type FormEvent } from "react";
import { BookOpen, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { ConfirmButton } from "@/components/ui/ConfirmButton";
import { useAuth } from "@/features/auth/auth-context";
import { RequirePermission } from "@/features/auth/RequirePermission";
import {
  useCreateKb,
  useDeleteKb,
  useKbArticles,
  useUpdateKb,
} from "./hooks";
import { getKb, type KbArticle, type KbArticleDetail } from "./api";

const PAGE_SIZE = 10;

export default function KnowledgeBasePage() {
  const { can } = useAuth();
  const canManage = can("ticket:update");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const { data } = useKbArticles({ search: search.trim(), page });
  const remove = useDeleteKb();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<KbArticleDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  async function openEdit(a: KbArticle) {
    await run(async () => {
      setEditing(await getKb(a.id));
      setFormOpen(true);
    });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <BookOpen className="h-6 w-6 text-primary" aria-hidden="true" />
            Knowledge base
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Solutions and how-tos, matched to tickets by the AI assistant.
          </p>
        </div>
        <RequirePermission permission="ticket:update">
          <Button onClick={() => { setEditing(null); setFormOpen(true); }}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            New article
          </Button>
        </RequirePermission>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <Input
          aria-label="Search articles"
          placeholder="Search the knowledge base"
          className="pl-8"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 font-medium">Article</th>
              <th className="px-4 py-2.5 font-medium">Category</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Updated</th>
              <th className="px-4 py-2.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  No articles found.
                </td>
              </tr>
            )}
            {data?.items.map((a) => (
              <tr key={a.id} className="border-t border-border">
                <td className="px-4 py-3">
                  <div className="font-medium">{a.title}</div>
                  {a.summary && <div className="text-xs text-muted-foreground">{a.summary}</div>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{a.category_code ?? "—"}</td>
                <td className="px-4 py-3">
                  <Badge tone={a.is_published ? "success" : "muted"}>
                    {a.is_published ? "Published" : "Draft"}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{formatDateTime(a.updated_at)}</td>
                <td className="px-4 py-3">
                  {canManage && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" aria-label={`Edit ${a.title}`} onClick={() => void openEdit(a)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <ConfirmButton aria-label={`Delete ${a.title}`} title="Delete article?" message={<>Delete <span className="font-medium text-foreground">{a.title}</span>?</>} onConfirm={() => run(() => remove.mutateAsync(a.id))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </ConfirmButton>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{data ? `${data.total} article${data.total === 1 ? "" : "s"}` : ""}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <span>Page {page} of {totalPages}</span>
          <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>

      <ArticleDialog open={formOpen} onClose={() => setFormOpen(false)} editing={editing} />
    </div>
  );
}

const CATEGORIES = ["hardware", "software", "network", "firewall", "server", "cloud", "email", "backup", "security", "amc", "other"];

function ArticleDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: KbArticleDetail | null;
}) {
  const create = useCreateKb();
  const update = useUpdateKb();
  const [form, setForm] = useState({ title: "", summary: "", body: "", category_code: "", is_published: false });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? {
            title: editing.title,
            summary: editing.summary ?? "",
            body: editing.body,
            category_code: editing.category_code ?? "",
            is_published: editing.is_published,
          }
        : { title: "", summary: "", body: "", category_code: "", is_published: false },
    );
    setError(null);
  }, [open, editing]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body = {
      title: form.title.trim(),
      summary: form.summary.trim() || null,
      body: form.body,
      category_code: form.category_code || null,
      is_published: form.is_published,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the article.");
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={editing ? "Edit article" : "New article"}>
      <form onSubmit={submit} className="space-y-3">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
        )}
        <Field label="Title" htmlFor="kb_title">
          <Input id="kb_title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        </Field>
        <Field label="Summary" htmlFor="kb_summary">
          <Input id="kb_summary" value={form.summary} onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))} />
        </Field>
        <Field label="Body" htmlFor="kb_body">
          <textarea
            id="kb_body"
            rows={6}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" htmlFor="kb_cat">
            <Select id="kb_cat" value={form.category_code} onChange={(e) => setForm((f) => ({ ...f, category_code: e.target.value }))}>
              <option value="">None</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </Select>
          </Field>
          <Field label="Status" htmlFor="kb_pub">
            <Select
              id="kb_pub"
              value={form.is_published ? "published" : "draft"}
              onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.value === "published" }))}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!form.title.trim() || !form.body.trim() || create.isPending || update.isPending}>
            {editing ? "Save changes" : "Create article"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
